import React from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";

import type { TCommunicationsCall } from "../../../../../services/api-communications";
import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";
import { PhoneNumberText, PrimaryButton } from "../../../../common/native";
import { styles } from "../HomeConversations.styles";
import { getCallStatusBadgeStyle } from "../utils/formatters";

type CallDetailCardProps = {
  showCallDetail: boolean;
  callDetailLoading: boolean;
  callDetailError: string | null;
  selectedCall: TCommunicationsCall | null;
  selectedCallHeaderLabel: string;
  canRequestHangup: boolean;
  hangupLoading: boolean;
  assignSubmitting: boolean;
  onHangupPress: () => void;
  onAssignToPatient: (call: TCommunicationsCall) => void;
  formatDuration: (durationSeconds?: number | null) => string;
  formatDateTime: (value?: string | null) => string;
};

const CallDetailCard: React.FC<CallDetailCardProps> = ({
  showCallDetail,
  callDetailLoading,
  callDetailError,
  selectedCall,
  selectedCallHeaderLabel,
  canRequestHangup,
  hangupLoading,
  assignSubmitting,
  onHangupPress,
  onAssignToPatient,
  formatDuration,
  formatDateTime,
}) => {
  const callStatusBadge = getCallStatusBadgeStyle(selectedCall?.status);
  const callStatusLabel = (selectedCall?.status || "unknown")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");

  return (
    <View style={styles.card}>
      <View style={styles.rowBetween}>
        <Text style={styles.cardTitle}>Call Detail</Text>
        {selectedCall ? (
          <View
            style={[
              styles.statusBadge,
              {
                backgroundColor: callStatusBadge.backgroundColor,
                borderColor: callStatusBadge.borderColor,
              },
            ]}
          >
            <Text
              style={[
                styles.statusBadgeText,
                { color: callStatusBadge.textColor },
              ]}
            >
              {callStatusLabel}
            </Text>
          </View>
        ) : null}
      </View>

      {!showCallDetail ? (
        <Text style={styles.emptyText}>
          Select a call from the inbox to view details and linked notes.
        </Text>
      ) : callDetailLoading ? (
        <View style={styles.loadingInlineRow}>
          <ActivityIndicator size="small" color={ADMIN_TOKENS.color.accent} />
          <Text style={styles.loadingText}>Loading call detail...</Text>
        </View>
      ) : callDetailError ? (
        <Text style={styles.inlineErrorText}>{callDetailError}</Text>
      ) : selectedCall ? (
        <View style={styles.sectionStack}>
          <Text style={styles.callNumberText}>{selectedCallHeaderLabel}</Text>
          <Text style={styles.metaText}>
            Call from{" "}
            <PhoneNumberText
              value={selectedCall.from_e164}
              style={styles.metaText}
            />{" "}
            to{" "}
            <PhoneNumberText
              value={selectedCall.to_e164}
              style={styles.metaText}
            />
          </Text>
          <Text style={styles.metaText}>
            Duration: {formatDuration(selectedCall.duration_seconds)}
          </Text>
          <Text style={styles.metaText}>
            Started: {formatDateTime(selectedCall.started_at)}
          </Text>
          <Text style={styles.metaText}>
            Answered: {formatDateTime(selectedCall.answered_at)}
          </Text>
          <Text style={styles.metaText}>
            Ended: {formatDateTime(selectedCall.ended_at)}
          </Text>
          <PrimaryButton
            text="Hangup Call"
            onPress={onHangupPress}
            loading={hangupLoading}
            disabled={!canRequestHangup || hangupLoading}
            fullWidth
          />
          <Pressable
            style={[
              styles.callAgainButton,
              assignSubmitting && styles.callAgainButtonDisabled,
            ]}
            onPress={() => onAssignToPatient(selectedCall)}
            disabled={assignSubmitting}
            accessibilityLabel={`Assign call ${selectedCall.call_uuid} to patient`}
          >
            <Text style={styles.callAgainButtonText}>Assign to Patient</Text>
          </Pressable>
        </View>
      ) : (
        <Text style={styles.emptyText}>Call details unavailable.</Text>
      )}
    </View>
  );
};

export default CallDetailCard;
